import { useMemo } from 'react'

import { AVAILABLE_REDIRECTS, setSignedInApp } from '@scoir/redirects'

import { hasBearerToken } from 'common/bearer'
import { useFnOnce } from './use-fn-once'
import { IUserStartupProps } from './types'

interface IuseInitializeReturn {
  isInitialized: boolean
  isNotAuthed: boolean
}

/**
 * This hook will run all the startup functions for the app.
 * First it will load the feature-toggles
 * Then it will load the user (if there is a bearer token)
 * Then it will load things it needs to show the app, setting isInitialied to true
 * Then it will load additional things
 * It will return notAuthed if there is no bearer token
 * @param param0 {IUserStartupProps}
 * @returns {IuseInitializeReturn}
 */
export const useInitialize = ({
  userId,
  userScid,
  fetchFeatureToggles,
  fetchUser,
  hasCollege,
  fetchCollege,
  fetchCurrentAppCycle,
}: IUserStartupProps): IuseInitializeReturn => {
  // make sure we have a bearer token, this is set in auth-web
  const hasToken = useMemo(() => hasBearerToken(), [])

  // Load feature toggles
  const hasFetchedFeatures = useFnOnce(fetchFeatureToggles)

  // Load user if we can
  useFnOnce(fetchUser, hasToken && !userId && hasFetchedFeatures)

  // Check if we're signed in, once the user is fetched it will have a userId here
  const isAuthed = !!userId

  // Load college if we havent
  const hasFetchedCollege = useFnOnce(() => fetchCollege(userScid as number), isAuthed && !hasCollege)

  // Load App Cycle once we're signed in
  const hasFetchedAppCycle = useFnOnce(fetchCurrentAppCycle, isAuthed)

  // The app can be shown once these loaders have been called
  const isInitialized = hasFetchedAppCycle && hasFetchedCollege

  // Set a signed-in app in @scoir/redirects local storage
  useFnOnce(() => Promise.resolve(setSignedInApp('admissions', AVAILABLE_REDIRECTS.COLLEGE_PROFILE)), isAuthed)

  return {
    isInitialized,
    isNotAuthed: !hasToken,
  }
}
